/**
 * DeviceToken Service
 *
 * Manages anonymous device authentication tokens.
 * Tokens are generated automatically and used to authenticate devices
 * with the backend without requiring manual approval.
 *
 * - Tokens are persisted in localStorage
 * - Tokens can be revoked by the server (returns 401)
 * - Tokens are automatically refreshed on revocation
 */

import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = '@efflux:deviceToken';
const LOG_PREFIX = '[DeviceToken]';

class DeviceTokenService {
  private token: string | null = null;

  /**
   * Gets the current device token.
   * Generates a new one if it doesn't exist.
   */
  getToken(): string {
    if (this.token) {
      return this.token;
    }

    // Try to load from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      console.log(`${LOG_PREFIX} ✅ Loaded existing token from storage`);
      this.token = stored;
      return stored;
    }

    // Generate new token
    return this.generateToken();
  }

  /**
   * Generates a new device token and persists it.
   */
  generateToken(): string {
    const newToken = uuidv4();
    this.token = newToken;
    localStorage.setItem(STORAGE_KEY, newToken);
    // Pentest R8-01: NÃO logar o token completo. É a credencial de auth do
    // device — o console é capturado por sinks persistentes (RemoteLogger +
    // LogRocket). Loga só o prefixo, igual ao padrão de useRemoteOpsInit.
    console.log(
      `${LOG_PREFIX} 🔑 Generated new device token: ${newToken.slice(0, 8)}…`,
    );
    return newToken;
  }

  /**
   * Refreshes the device token (generates a new one).
   * Called when the server revokes the current token.
   */
  refreshToken(): string {
    console.log(`${LOG_PREFIX} 🔄 Refreshing device token (previous revoked)`);
    return this.generateToken();
  }

  /**
   * Clears the device token (for testing/reset).
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem(STORAGE_KEY);
    console.log(`${LOG_PREFIX} 🗑️ Cleared device token`);
  }

  /**
   * Gets the current token without generating a new one.
   * Returns null if no token exists.
   */
  getTokenOrNull(): string | null {
    return this.token || localStorage.getItem(STORAGE_KEY);
  }
}

export const deviceTokenService = new DeviceTokenService();
export default deviceTokenService;
